/**
 * MultiSelect — inline searchable checkbox list (multi-value).
 * For Media Devoted | Created By Ciro Autiero
 * File edited by By James Alexandrov
 */

import { FC, useMemo, useState } from "react";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

import { usePinnedOptions } from "./multi-select/use-pinned-options";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  /** Cap how many rows render at once (selected pinned first). Search still
   *  scans every option; unset = render all. Keeps huge lists responsive. */
  maxRender?: number;
  /** Tailwind max-height for the scroll area (default max-h-40 ≈ 7 rows). */
  maxHeightClass?: string;
  /** Show "Include all" / "Exclude all" quick actions above the list. */
  bulkActions?: boolean;
  /** Float selected rows to the top of the list (order otherwise preserved). */
  pinSelected?: boolean;
}

export const MultiSelect: FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  searchPlaceholder = "Search…",
  emptyText = "No options",
  disabled,
  className,
  maxRender,
  maxHeightClass = "max-h-40",
  bulkActions,
  pinSelected,
}) => {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.label.toLowerCase().includes(s)) : options;
  }, [options, q]);

  // Selected rows float to the top, from a snapshot rather than a live sort —
  // see usePinnedOptions. Every change below goes through `emit` so the
  // component's own echo is not read as an outside re-seed.
  const { shown, emit } = usePinnedOptions({
    filtered,
    value,
    onChange,
    pinKey: `${options.length}|${q}`,
    pinSelected,
    maxRender,
  });

  const toggle = (v: string) =>
    emit(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  // Bulk actions operate on the current search filter (the matching rows).
  const includeAll = () =>
    emit(Array.from(new Set([...value, ...filtered.map((o) => o.value)])));
  const excludeAll = () => {
    const inFilter = new Set(filtered.map((o) => o.value));
    emit(value.filter((v) => !inFilter.has(v)));
  };

  return (
    <div className={cn("rounded-md border border-input bg-background", disabled && "opacity-50", className)}>
      <div className="relative border-b border-input">
        <input
          value={q}
          disabled={disabled}
          placeholder={searchPlaceholder}
          onChange={(e) => setQ(e.target.value)}
          className="w-full bg-transparent px-2 py-1 pr-6 text-xs outline-none"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ("")}
            aria-label="Clear search"
            className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      {bulkActions && (
        <div className="flex gap-3 border-b border-input px-2 py-1 text-[10px]">
          <button
            type="button"
            onClick={includeAll}
            disabled={disabled}
            className="text-primary hover:underline disabled:opacity-50"
          >
            Include all
          </button>
          <button
            type="button"
            onClick={excludeAll}
            disabled={disabled}
            className="text-muted-foreground hover:underline disabled:opacity-50"
          >
            Exclude all
          </button>
        </div>
      )}
      <div className={cn(maxHeightClass, "overflow-y-auto p-1")}>
        {shown.length === 0 ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">{emptyText}</p>
        ) : (
          shown.map((o) => (
            <label
              key={o.value}
              className="flex items-center gap-2 rounded px-1 py-1 text-xs hover:bg-muted/60"
            >
              <input
                type="checkbox"
                checked={value.includes(o.value)}
                disabled={disabled}
                onChange={() => toggle(o.value)}
              />
              <span className="truncate">{o.label}</span>
            </label>
          ))
        )}
      </div>
      {options.length > 0 && (
        <p className="border-t border-input px-2 py-1 text-[10px] text-muted-foreground">
          Showing {shown.length} of {options.length}
          {shown.length < filtered.length ? " — refine search" : ""}
        </p>
      )}
    </div>
  );
};

export default MultiSelect;
